import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IntelligenceShell, MonoNum } from '../components/intelligence/IntelligenceShell';
import { useIntelligenceReport, KpiCard, ChartCard, RangeControl, ReportHeader, ReportSkeleton, downloadCsv, CARD } from '../components/intelligence/ReportKit';
import { AiSuggestion } from '../components/intelligence/AiSuggestion';
import { Megaphone, Download, Lock, Globe, ArrowRight, Check, Loader2 } from 'lucide-react';
import apiV2 from '../utils/apiV2';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { toast } from 'sonner';

const AgeBar = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="space-y-1.5">
      {data.map((d) => (
        <div key={d.bucket} className="flex items-center gap-3">
          <div className="w-20 text-xs font-semibold text-slate-600">{d.bucket}</div>
          <div className="flex-1 h-4 bg-slate-100/70 rounded overflow-hidden">
            <div className={`h-full rounded ${d.bucket === 'Unknown' ? 'bg-slate-300' : 'bg-[#60A5FA]'}`} style={{ width: `${Math.max(2, (100 * d.count) / max)}%` }} />
          </div>
          <MonoNum className="w-12 text-xs font-bold text-slate-700 text-right">{d.count.toLocaleString()}</MonoNum>
        </div>
      ))}
    </div>
  );
};

const SegmentCard = ({ seg, onExport, exporting, delay }) => (
  <div className={`${CARD} p-5 intel-rise flex flex-col`} style={{ animationDelay: `${delay}ms` }} data-testid={`audience-segment-${seg.id}`}>
    <div className="flex items-start justify-between gap-3 mb-1">
      <span className="font-bold text-sm text-slate-900">{seg.name}</span>
      <MonoNum className="text-lg font-extrabold text-[#2563EB]">{seg.count.toLocaleString()}</MonoNum>
    </div>
    <p className="text-xs text-slate-500 leading-relaxed flex-1">{seg.desc}</p>
    <button
      onClick={() => onExport(seg.id)}
      disabled={seg.count === 0 || exporting === seg.id}
      data-testid={`audience-export-${seg.id}`}
      className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8] disabled:text-slate-300 disabled:cursor-not-allowed transition-colors"
    >
      {exporting === seg.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      Export CSV
    </button>
  </div>
);

export default function AudiencesReport() {
  const [months, setMonths] = useState(12);
  const [exporting, setExporting] = useState(null);
  const { data, loading } = useIntelligenceReport('audiences', months);
  const { formatMoney } = useOperatorLocale();
  const navigate = useNavigate();
  const orgId = localStorage.getItem('organizationId');

  const exportSegment = async (segId) => {
    setExporting(segId);
    try {
      const res = await apiV2.get(`/organizations/${orgId}/intelligence/audiences/export?segment=${segId}&months=${months}`);
      downloadCsv([res.data.headers, ...res.data.rows], `audience-${segId}.csv`);
      toast.success(`${res.data.rows.length.toLocaleString()} contacts exported`);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const k = data?.kpis;

  return (
    <IntelligenceShell>
      <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-6xl mx-auto" data-testid="audiences-report">
        <ReportHeader
          title="Audiences"
          blurb="Every reachable contact your business has earned — purchasers, waiver signers, opt-ins and export-ready segments for your next campaign."
          right={<><AiSuggestion report="audiences" months={months} /><RangeControl months={months} setMonths={setMonths} prefix="audiences" /></>}
        />
        {loading || !data ? <ReportSkeleton /> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
              <KpiCard label="Total contacts" value={k.totalContacts.toLocaleString()} sub={`${k.purchasers.toLocaleString()} purchasers · ${k.signersOnly.toLocaleString()} guests`} testid="aud-kpi-total" />
              <KpiCard label="Emails on file" value={k.withEmail.toLocaleString()} testid="aud-kpi-emails" delay={40} />
              <KpiCard label="Phones on file" value={k.withPhone.toLocaleString()} sub="OTP-verified at signing" testid="aud-kpi-phones" delay={80} />
              <KpiCard label="Marketing opt-ins" value={k.optedIn.toLocaleString()} sub={`${k.optInRate}% opt-in rate`} testid="aud-kpi-optins" delay={120} />
            </div>

            <div className="mb-5 intel-rise">
              <h2 className="text-base font-extrabold tracking-tight text-slate-900">Segments</h2>
              <p className="text-xs text-slate-400 mt-1 font-semibold">Export any segment as CSV — upload straight into Meta Custom Audiences or your email tool. Only message opted-in contacts.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {data.segments.map((seg, i) => (
                <SegmentCard key={seg.id} seg={seg} onExport={exportSegment} exporting={exporting} delay={i * 50} />
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
              <ChartCard title="Age breakdown" testid="aud-age-breakdown">
                <AgeBar data={data.ageBreakdown} />
                <p className="mt-4 text-[11px] text-slate-400">
                  Captured in the waiver flow — {k.ageKnown.toLocaleString()} of {k.totalContacts.toLocaleString()} contacts have a confirmed age range.
                </p>
              </ChartCard>
              <ChartCard title="Audiences by service" testid="aud-service-audiences">
                {data.serviceAudiences.length === 0 ? (
                  <p className="text-sm text-slate-400">No service data yet.</p>
                ) : (
                  <div className="space-y-1.5">
                    {data.serviceAudiences.map((s) => {
                      const max = data.serviceAudiences[0]?.contacts || 1;
                      return (
                        <div key={s.service} className="flex items-center gap-3">
                          <div className="w-36 text-xs font-semibold text-slate-600 truncate">{s.service}</div>
                          <div className="flex-1 h-4 bg-slate-100/70 rounded overflow-hidden">
                            <div className="h-full bg-[#2563EB] rounded" style={{ width: `${Math.max(3, (100 * s.contacts) / max)}%` }} />
                          </div>
                          <MonoNum className="w-12 text-xs font-bold text-slate-700 text-right">{s.contacts}</MonoNum>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="mt-4 text-[11px] text-slate-400">Who books what — natural interest segments for targeted offers.</p>
              </ChartCard>
            </div>

            {/* Web audience — beacon-gated */}
            {data.web.beaconInstalled ? (
              <div className={`${CARD} p-6 mb-6`} data-testid="aud-web-section">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-9 h-9 rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center">
                    <Globe className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-bold text-sm text-slate-900">Website audience</div>
                    <div className="text-[11px] text-slate-400">From your BookingFlows Beacon — anonymous, sizes only</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Visitors · 30 days', value: data.web.visitors30d, testid: 'aud-web-visitors' },
                    { label: 'Engaged the widget', value: data.web.engagedVisitors, testid: 'aud-web-engaged' },
                    { label: 'Retargeting pool', value: data.web.retargetPool, sub: 'engaged, never booked', testid: 'aud-web-pool' },
                  ].map((m) => (
                    <div key={m.label} data-testid={m.testid}>
                      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">{m.label}</div>
                      <MonoNum className="text-xl font-extrabold text-[#2563EB]">{(m.value ?? 0).toLocaleString()}</MonoNum>
                      {m.sub && <div className="text-[11px] text-slate-400">{m.sub}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={`${CARD} p-6 mb-6 flex items-center gap-4 flex-wrap`} data-testid="aud-web-locked">
                <div className="w-11 h-11 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0">
                  <Lock className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-bold text-sm text-slate-900 mb-0.5">Website audiences locked</div>
                  <p className="text-xs text-slate-500 leading-relaxed">Install the BookingFlows Beacon to see visitors who browsed your site but never booked — your anonymous retargeting pool.</p>
                </div>
                <button
                  onClick={() => navigate('/intelligence/website-analytics')}
                  data-testid="aud-web-unlock-btn"
                  className="px-4 py-2.5 rounded-xl bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8] transition-colors flex items-center gap-1.5"
                >
                  Install Beacon <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}

            <ChartCard title="Top contacts" testid="aud-contacts" className="mb-6">
              {data.topContacts.length === 0 ? (
                <p className="text-sm text-slate-400">No contacts yet — they'll appear here as bookings and waivers come in.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        <th className="pb-2 pr-2">Contact</th>
                        <th className="pb-2 px-2">Age</th>
                        <th className="pb-2 px-2">Opt-in</th>
                        <th className="pb-2 px-2 text-right">Bookings</th>
                        <th className="pb-2 px-2 text-right">Revenue</th>
                        <th className="pb-2 pl-2 text-right">Last activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topContacts.map((c, i) => (
                        <tr key={i} className="border-t border-slate-50">
                          <td className="py-2.5 pr-2">
                            <div className="font-semibold text-slate-800 text-[13px]">{c.name}</div>
                            <div className="text-[11px] text-slate-400 font-mono">{c.email || c.phone}</div>
                          </td>
                          <td className="py-2.5 px-2 text-xs text-slate-500">{c.ageRange || '—'}</td>
                          <td className="py-2.5 px-2">
                            {c.optedIn ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold ring-1 ring-emerald-100">
                                <Check className="w-3 h-3" /> Yes
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-2 text-right"><MonoNum className="font-bold">{c.bookings}</MonoNum></td>
                          <td className="py-2.5 px-2 text-right"><MonoNum className="font-bold text-slate-800">{c.revenue > 0 ? formatMoney(c.revenue) : '—'}</MonoNum></td>
                          <td className="py-2.5 pl-2 text-right text-xs text-slate-400">{c.lastActivity ? new Date(c.lastActivity).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </ChartCard>

            <div className="text-[11px] text-slate-400 flex items-center gap-1.5" data-testid="aud-consent-note">
              <Megaphone className="w-3.5 h-3.5" />
              Contacts are collected through bookings and signed waivers with transparent consent capture. Exports include opt-in status — only message contacts who opted in.
            </div>
          </>
        )}
      </div>
    </IntelligenceShell>
  );
}
